import { ActionCreator } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { AccountingApi } from '../../../api/accountingApi'
import { toastMsg } from '../../../components/Toast/toastMsg'
import { IPagination } from '../../../types/pagination'
import { ProjectsState } from '../reducers'
import { IDirectCostInvoice, IPositionPayment, IProjectInvoice } from '../types/invoice'
import * as constants from './constants'


export type AccountingThunkResult<R> = ThunkAction<Promise<R>, ProjectsState, null, AccountingActions>
export type AccountingThunkDispatchType = ThunkDispatch<ProjectsState, null, AccountingActions>

export interface AccountingHasErrored {
  type: constants.ACCOUNTING_HAS_ERRORED,
  hasErrored: boolean
}

export interface GetProjectInvoicesSuccess {
  type: constants.GET_INVOICES_SUCCESS
  invoices: IProjectInvoice[]
}

export interface GetProjectInvoiceSuccess {
  type: constants.GET_INVOICE_SUCCESS
  invoice: IProjectInvoice
}

export interface CreateProjectInvoiceSuccess {
  type: constants.CREATE_INVOICE_SUCCESS
  invoice: IProjectInvoice
}

export interface UpdateProjectInvoiceSuccess {
  type: constants.UPDATE_INVOICE_SUCCESS
  invoice: IProjectInvoice
}

export interface DeleteProjectInvoiceSuccess {
  type: constants.DELETE_INVOICE_SUCCESS
  invoice: IProjectInvoice
}

export interface SelectProjectInvoice {
  type: constants.SET_SELECTED_INVOICE
  invoice: IProjectInvoice
}

export interface SortInvoices {
  type: constants.SORT_INVOICES,
  sort: { name: string, direction: string }
}

export interface SortPtc {
  type: constants.SORT_PTC,
  sort: { name: string, direction: string }
}

export interface GetPaginatedPaymentsSuccess {
  type: constants.GET_PAGINATED_PAYMENTS_SUCCESS
  data: IPagination<IPositionPayment[]>
}

export interface GetPaymentsSuccess {
  type: constants.GET_PAYMENTS_SUCCESS
  data: IPositionPayment[]
}

export interface SortPayments {
  type: constants.SORT_PAYMENTS,
  sort: { name: string, direction: string }
}

export interface SetPaginatedPaymentsePage {
  type: constants.SET_PAGINATED_PAYMENTS_ACTIVE_PAGE
  page: number
}

export interface SetPaginatedInvoicesePage {
  type: constants.SET_PAGINATED_INVOICES_ACTIVE_PAGE
  page: number
}

export interface GetPaginatedProjectInvoicesSuccess {
  type: constants.GET_PAGINATED_INVOICES_SUCCESS
  data: IPagination<IProjectInvoice[]>
}

export interface SelectAccountingTab {
  type: constants.SELECT_ACCOUTING_TAB
  tab: string
}

/* Scheduled Payments */
export interface GetDirectCostInvoicesSuccess {
  type: constants.GET_DIRECT_COST_INVOICES_SUCCESS
  invoices: IDirectCostInvoice[]
}

export interface GetDirectCostInvoiceSuccess {
  type: constants.GET_DIRECT_COST_INVOICE_SUCCESS
  invoice: IDirectCostInvoice
}

export interface CreateDirectCostInvoiceSuccess {
  type: constants.CREATE_DIRECT_COST_INVOICE_SUCCESS
  invoice: IDirectCostInvoice
}

export interface UpdateDirectCostInvoiceSuccess {
  type: constants.UPDATE_DIRECT_COST_INVOICE_SUCCESS
  invoice: IDirectCostInvoice
}

export interface DeleteDirectCostInvoiceSuccess {
  type: constants.DELETE_DIRECT_COST_INVOICE_SUCCESS
  invoice: IDirectCostInvoice
}

export interface SelectDirectCostInvoice {
  type: constants.SET_SELECTED_DIRECT_COST_INVOICE
  invoice: IDirectCostInvoice
}

export interface SortDirectCostInvoices {
  type: constants.SORT_DIRECT_COST_INVOICES,
  sort: { name: string, direction: string }
}

export interface GetLatestPaymentsSuccess {
  type: constants.GET_LATEST_PAYMENTS_SUCCESS
  invoices: IProjectInvoice[]
}


export const accountingHasErrored = (hasErrored: boolean, error: Error, action?: string): AccountingHasErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.ACCOUNTING_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

export const getProjectInvoices: ActionCreator<AccountingThunkResult<IProjectInvoice[]>>
  = (projectId: number | string, queryParams: Record<string, string>): AccountingThunkResult<IProjectInvoice[]> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.getProjectInvoices(projectId, queryParams)
        .then(response => {
          dispatch(getProjectInvoicesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_INVOICES))
          return {} as IProjectInvoice[]
        })
    }
  }


export const getProjectInvoicesSuccess = (invoices: IProjectInvoice[]): GetProjectInvoicesSuccess => {
  return {
    type: constants.GET_INVOICES_SUCCESS,
    invoices
  }
}

export const getProjectInvoice: ActionCreator<AccountingThunkResult<IProjectInvoice>>
  = (projectId: string | number, invoice_id: string | number,): AccountingThunkResult<IProjectInvoice> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.getProjectInvoice(projectId, invoice_id)
        .then(response => {
          dispatch(getProjectInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_INVOICE))
          return {} as IProjectInvoice
        })
    }
  }

export const getProjectInvoiceSuccess = (invoice: IProjectInvoice): GetProjectInvoiceSuccess => {
  return {
    type: constants.GET_INVOICE_SUCCESS,
    invoice
  }
}

export const getPaginatedProjectInvoice: ActionCreator<AccountingThunkResult<IPagination<IProjectInvoice[]>>>
  = (projectId: number | string, queryParams: Record<string, string>): AccountingThunkResult< IPagination<IProjectInvoice[]>> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.getPaginatedProjectInvoices(projectId, queryParams)
        .then(response => {
          dispatch(getPaginatedProjectInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_PAGINATED_INVOICES))
          return {} as  IPagination<IProjectInvoice[]>
        })
    }
  }


export const getPaginatedProjectInvoiceSuccess = (data: IPagination<IProjectInvoice[]>): GetPaginatedProjectInvoicesSuccess => {
  return {
    type: constants.GET_PAGINATED_INVOICES_SUCCESS,
    data
  }
}

export const createProjectInvoice: ActionCreator<AccountingThunkResult<IProjectInvoice>>
  = (projectId: string | number, invoice: IProjectInvoice): AccountingThunkResult<IProjectInvoice> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.createProjectInvoice(projectId, invoice)
        .then(response => {
          dispatch(createProjectInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.CREATE_INVOICE))
          return {} as IProjectInvoice
        })
    }
  }

export const createProjectInvoiceSuccess = (invoice: IProjectInvoice): CreateProjectInvoiceSuccess => {
  toastMsg(`Create ${invoice.number} successfully.`, constants.CREATE_INVOICE_SUCCESS, 'success')
  return {
    type: constants.CREATE_INVOICE_SUCCESS,
    invoice
  }
}

export const updateProjectInvoice: ActionCreator<AccountingThunkResult<IProjectInvoice>>
  = (projectId: string | number, invoice: IProjectInvoice): AccountingThunkResult<IProjectInvoice> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.updateProjectInvoice(projectId, invoice)
        .then(response => {
          dispatch(updateProjectInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.UPDATE_INVOICE))
          return {} as IProjectInvoice
        })
    }
  }

export const updateProjectInvoiceSuccess = (invoice: IProjectInvoice): UpdateProjectInvoiceSuccess => {
  toastMsg(`Updated ${invoice.number} successfully.`, constants.UPDATE_INVOICE_SUCCESS, 'success')
  return {
    type: constants.UPDATE_INVOICE_SUCCESS,
    invoice
  }
}

export const deleteProjectInvoice: ActionCreator<AccountingThunkResult<string | number | void>>
  = (projectId: string | number, invoice: IProjectInvoice): AccountingThunkResult<string | number | void> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.deleteProjectInvoice(projectId, invoice)
        .then(response => {
          dispatch(deleteProjectInvoiceSuccess(invoice))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.DELETE_INVOICE))
        })
    }
  }

export const deleteProjectInvoiceSuccess = (invoice: IProjectInvoice): DeleteProjectInvoiceSuccess => {
  toastMsg(`Deleted ${invoice.number} successfully.`, constants.DELETE_INVOICE_SUCCESS, 'success')
  return {
    type: constants.DELETE_INVOICE_SUCCESS,
    invoice
  }
}

export const setSelectedInvoice = (invoice: IProjectInvoice): SelectProjectInvoice => {
  return {
    type: constants.SET_SELECTED_INVOICE,
    invoice
  }
}

export const sortInvoices = (name: string, direction: string): SortInvoices => {
  return {
    type: constants.SORT_INVOICES,
    sort: { name: name, direction: direction }
  }
}

export const getPositionPayments: ActionCreator<AccountingThunkResult<IPagination<IPositionPayment[]> | IPositionPayment[]>>
  = (projectId: number | string, queryParams?: Record<string, string>): AccountingThunkResult< IPagination<IPositionPayment[]> | IPositionPayment[]> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.getPositionPayments(projectId, queryParams)
        .then(response => {
          if (queryParams && 'page' in queryParams) {
            const _resp: IPagination<IPositionPayment[]> = response as IPagination<IPositionPayment[]>
            dispatch(getPaginatedPositionPaymentsSuccess(_resp))
          } else {
            dispatch(getPositionPaymentsSuccess(response as IPositionPayment[]))
          }
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_PAYMENTS))
          return {} as IPagination<IPositionPayment[]> | {} as IPositionPayment[]
        })
    }
  }


export const getPositionPaymentsSuccess = (data: IPositionPayment[]): GetPaymentsSuccess => {
  return {
    type: constants.GET_PAYMENTS_SUCCESS,
    data
  }
}

export const getPaginatedPositionPaymentsSuccess = (data: IPagination<IPositionPayment[]>): GetPaginatedPaymentsSuccess => {
  return {
    type: constants.GET_PAGINATED_PAYMENTS_SUCCESS,
    data
  }
}

export const sortPayments = (name: string, direction: string): SortPayments => {
  return {
    type: constants.SORT_PAYMENTS,
    sort: { name: name, direction: direction }
  }
}

export const setPaginatedPaymentsActivePage = (page: number): SetPaginatedPaymentsePage => {
  return {
    type: constants.SET_PAGINATED_PAYMENTS_ACTIVE_PAGE,
    page: page
  }
}

export const setPaginatedInvoicesActivePage = (page: number): SetPaginatedInvoicesePage => {
  return {
    type: constants.SET_PAGINATED_INVOICES_ACTIVE_PAGE,
    page: page
  }
}

export const selectAccountingTab = (tab: string): SelectAccountingTab => {
  return {
    type: constants.SELECT_ACCOUTING_TAB,
    tab: tab
  }
}

/* Scheduled Payments */
export const getDirectCostInvoices: ActionCreator<AccountingThunkResult<IDirectCostInvoice[]>>
  = (projectId: number | string, queryParams?: Record<string, string>): AccountingThunkResult<IDirectCostInvoice[]> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.getDirectCostInvoices(projectId, queryParams)
        .then(response => {
          dispatch(getDirectCostInvoicessSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_DIRECT_COST_INVOICES))
          return {} as IDirectCostInvoice[]
        })
    }
  }

export const getDirectCostInvoicessSuccess = (invoices: IDirectCostInvoice[]): GetDirectCostInvoicesSuccess => {
  return {
    type: constants.GET_DIRECT_COST_INVOICES_SUCCESS,
    invoices
  }
}


export const getDirectCostInvoice: ActionCreator<AccountingThunkResult<IDirectCostInvoice>>
  = (projectId: string | number, invoice_id: string | number,): AccountingThunkResult<IDirectCostInvoice> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.getDirectCostInvoice(projectId, invoice_id)
        .then(response => {
          dispatch(getDirectCostInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_DIRECT_COST_INVOICE))
          return {} as IDirectCostInvoice
        })
    }
  }

export const getDirectCostInvoiceSuccess = (invoice: IDirectCostInvoice): GetDirectCostInvoiceSuccess => {
  return {
    type: constants.GET_DIRECT_COST_INVOICE_SUCCESS,
    invoice
  }
}

export const createDirectCostInvoice: ActionCreator<AccountingThunkResult<IDirectCostInvoice>>
  = (projectId: string | number, invoice: IDirectCostInvoice): AccountingThunkResult<IDirectCostInvoice> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.createDirectCostInvoice(projectId, invoice)
        .then(response => {
          dispatch(createDirectCostInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.CREATE_DIRECT_COST_INVOICE))
          return {} as IDirectCostInvoice
        })
    }
  }

export const createDirectCostInvoiceSuccess = (invoice: IDirectCostInvoice): CreateDirectCostInvoiceSuccess => {
  toastMsg(`Create ${invoice.number} successfully.`, constants.CREATE_DIRECT_COST_INVOICE_SUCCESS, 'success')
  return {
    type: constants.CREATE_DIRECT_COST_INVOICE_SUCCESS,
    invoice
  }
}

export const updateDirectCostInvoice: ActionCreator<AccountingThunkResult<IDirectCostInvoice>>
  = (projectId: string | number, invoice: IDirectCostInvoice): AccountingThunkResult<IDirectCostInvoice> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.updateDirectCostInvoice(projectId, invoice)
        .then(response => {
          dispatch(updateDirectCostInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.UPDATE_DIRECT_COST_INVOICE))
          return {} as IDirectCostInvoice
        })
    }
  }

export const updateDirectCostInvoiceSuccess = (invoice: IDirectCostInvoice): UpdateDirectCostInvoiceSuccess => {
  toastMsg(`Updated ${invoice.number} successfully.`, constants.UPDATE_DIRECT_COST_INVOICE_SUCCESS, 'success')
  return {
    type: constants.UPDATE_DIRECT_COST_INVOICE_SUCCESS,
    invoice
  }
}

export const deleteDirectCostInvoice: ActionCreator<AccountingThunkResult<string | number | void>>
  = (projectId: string | number, invoice: IDirectCostInvoice): AccountingThunkResult<string | number | void> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.deleteDirectCostInvoice(projectId, invoice)
        .then(response => {
          dispatch(deleteDirectCostInvoiceSuccess(invoice))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.DELETE_DIRECT_COST_INVOICE))
        })
    }
  }

export const deleteDirectCostInvoiceSuccess = (invoice: IDirectCostInvoice): DeleteDirectCostInvoiceSuccess => {
  toastMsg(`Deleted ${invoice.number} successfully.`, constants.DELETE_DIRECT_COST_INVOICE_SUCCESS, 'success')
  return {
    type: constants.DELETE_DIRECT_COST_INVOICE_SUCCESS,
    invoice
  }
}

export const setSelectedDirectCostInvoice = (invoice: IDirectCostInvoice): SelectDirectCostInvoice => {
  return {
    type: constants.SET_SELECTED_DIRECT_COST_INVOICE,
    invoice
  }
}

export const sortDirectCostInvoices = (name: string, direction: string): SortDirectCostInvoices => {
  return {
    type: constants.SORT_DIRECT_COST_INVOICES,
    sort: { name: name, direction: direction }
  }
}

export const getLatestPayments: ActionCreator<AccountingThunkResult<IProjectInvoice[]>>
  = (prId: number): AccountingThunkResult<IProjectInvoice[]> => {
    return async (dispatch: AccountingThunkDispatchType) => {
      return AccountingApi.getLatestPayments(prId)
        .then(response => {
          dispatch(getLatestPaymentsSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_LATEST_PAYMENTS))
          return {} as IProjectInvoice[]
        })
    }
  }

export const getLatestPaymentsSuccess = (invoices: IProjectInvoice[]): GetLatestPaymentsSuccess => {
  return {
    type: constants.GET_LATEST_PAYMENTS_SUCCESS,
    invoices
  }
}

export const sortPtc = (name: string, direction: string): SortPtc => {
  return {
    type: constants.SORT_PTC,
    sort: { name: name, direction: direction }
  }
}

export type AccountingActions
  = AccountingHasErrored
  | GetProjectInvoicesSuccess
  | GetProjectInvoiceSuccess
  | CreateProjectInvoiceSuccess
  | UpdateProjectInvoiceSuccess
  | DeleteProjectInvoiceSuccess
  | SelectProjectInvoice
  | SortInvoices
  | GetPaymentsSuccess
  | SortPayments
  | SetPaginatedPaymentsePage
  | SetPaginatedInvoicesePage
  | GetPaginatedProjectInvoicesSuccess
  | SelectAccountingTab
  | GetDirectCostInvoicesSuccess
  | GetDirectCostInvoiceSuccess
  | CreateDirectCostInvoiceSuccess
  | UpdateProjectInvoiceSuccess
  | UpdateDirectCostInvoiceSuccess
  | DeleteDirectCostInvoiceSuccess
  | SelectDirectCostInvoice
  | SortDirectCostInvoices
  | GetPaginatedPaymentsSuccess
  | GetLatestPaymentsSuccess
  | SortPtc
